import React, { useState } from 'react';
import { List, useTable } from '@refinedev/antd';
import { useInvalidate, useList } from '@refinedev/core';
import {
  Table,
  Space,
  Tag,
  Input,
  Select,
  Button,
  Modal,
  Form,
  InputNumber,
  Radio,
  message,
  Typography,
} from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { UserDto } from '@wellverse/shared-ts';
import { axiosInstance, API_BASE_URL } from '../../providers/dataProvider';

const { Text } = Typography;

const COIN_SOURCES = [
  'ADMIN_GRANT',
  'ADMIN_DEDUCT',
  'DAILY_TASK_CLAIM',
  'BUY_FREEZE_TOKEN',
  'STREAK_MILESTONE',
  'LEVEL_UP',
];

interface CoinTransactionRow {
  id: string;
  userId: string;
  amount: number;
  source: string;
  description?: string | null;
  balanceAfter: number;
  earnedAt: string;
  createdAt: string;
  user: {
    id: string;
    email?: string | null;
    phone?: string | null;
    fullName?: string | null;
  };
}

const sourceColor = (source: string) => {
  if (source === 'ADMIN_GRANT' || source === 'DAILY_TASK_CLAIM' || source === 'STREAK_MILESTONE' || source === 'LEVEL_UP') {
    return 'green';
  }
  if (source === 'ADMIN_DEDUCT' || source === 'BUY_FREEZE_TOKEN') {
    return 'red';
  }
  return 'default';
};

export const CoinTransactionList: React.FC = () => {
  const invalidate = useInvalidate();
  const [userIdFilter, setUserIdFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string | undefined>();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const { tableProps } = useTable<CoinTransactionRow>({
    resource: 'coin-transactions',
    filters: {
      permanent: [
        ...(userIdFilter
          ? [{ field: 'userId', operator: 'eq' as const, value: userIdFilter }]
          : []),
        ...(sourceFilter
          ? [{ field: 'source', operator: 'eq' as const, value: sourceFilter }]
          : []),
      ],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const { data: usersData } = useList<UserDto>({
    resource: 'users',
    pagination: { pageSize: 200 },
  });

  const userOptions = (usersData?.data ?? []).map((u) => ({
    value: u.id,
    label: `${u.fullName || 'User'} (${u.email || u.phone || u.id})`,
  }));

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ type: 'GRANT', amount: 100 });
    setModalOpen(true);
  };

  const submitAdjustment = async () => {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      await axiosInstance.post(`${API_BASE_URL}/admin/gamification/coin-transactions`, {
        userId: values.userId,
        type: values.type,
        amount: values.amount,
        reason: values.reason,
      });
      message.success(
        values.type === 'GRANT'
          ? `Granted ${values.amount} coins`
          : `Deducted ${values.amount} coins`,
      );
      setModalOpen(false);
      await invalidate({ resource: 'coin-transactions', invalidates: ['list'] });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Adjustment failed';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <List
      title="Coin Ledger"
      headerButtons={
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
          Adjust balance
        </Button>
      }
    >
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Filter by user ID"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value.trim())}
          style={{ width: 280 }}
          allowClear
        />
        <Select
          placeholder="Source"
          allowClear
          style={{ width: 200 }}
          value={sourceFilter}
          onChange={setSourceFilter}
          options={COIN_SOURCES.map((s) => ({ value: s, label: s }))}
        />
      </Space>

      <Table {...tableProps} rowKey="id" scroll={{ x: 960 }}>
        <Table.Column
          title="Time"
          dataIndex="earnedAt"
          width={160}
          render={(v: string) => dayjs(v).format('YYYY-MM-DD HH:mm')}
        />
        <Table.Column
          title="User"
          width={220}
          render={(_: unknown, row: CoinTransactionRow) => (
            <div>
              <div style={{ fontWeight: 500 }}>
                {row.user.fullName || '—'}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {row.user.email || row.user.phone || row.userId}
              </Text>
            </div>
          )}
        />
        <Table.Column
          title="Amount"
          dataIndex="amount"
          width={100}
          render={(amount: number) => (
            <Text style={{ color: amount >= 0 ? '#3ecf8e' : '#ff4d4f', fontWeight: 600 }}>
              {amount >= 0 ? `+${amount}` : amount}
            </Text>
          )}
        />
        <Table.Column
          title="Source"
          dataIndex="source"
          width={160}
          render={(source: string) => (
            <Tag color={sourceColor(source)}>{source}</Tag>
          )}
        />
        <Table.Column
          title="Reason"
          dataIndex="description"
          ellipsis
          render={(v: string | null) => v || '—'}
        />
        <Table.Column
          title="Balance after"
          dataIndex="balanceAfter"
          width={120}
        />
      </Table>

      <Modal
        title="Adjust coin balance"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={submitAdjustment}
        confirmLoading={submitting}
        okText="Submit"
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="userId"
            label="User"
            rules={[{ required: true, message: 'Select a user' }]}
          >
            <Select
              showSearch
              placeholder="Search user by name or email"
              options={userOptions}
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="type" label="Type" rules={[{ required: true }]}>
            <Radio.Group>
              <Radio.Button value="GRANT">Grant (+)</Radio.Button>
              <Radio.Button value="DEDUCT">Deduct (−)</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: 'Enter amount' },
              { type: 'number', min: 1, message: 'Minimum 1 coin' },
            ]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[
              { required: true, message: 'Reason is required for audit' },
              { min: 3, message: 'At least 3 characters' },
            ]}
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g. QA test grant for freeze purchase flow"
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </List>
  );
};
